import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import {
  CustomDestination,
  geocodeCity,
  addCustomDestination,
  updateCustomDestination,
  updateCustomDestinationEnrichment,
} from '../../utils/customDestinations';
import { destinationsAPI } from '../../services/api';

interface CustomDestinationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (destination: CustomDestination) => void;
  editingDestination?: CustomDestination | null;
}

const REGIONS = ['Europe', 'Asia', 'Americas', 'Africa', 'Oceania', 'Middle East'];
const BUDGET_LEVELS = ['budget', 'midrange', 'luxury'];

export const CustomDestinationModal: React.FC<CustomDestinationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingDestination,
}) => {
  const { t } = useTranslation('destinations');

  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [tagline, setTagline] = useState('');
  const [budgetLevel, setBudgetLevel] = useState('');
  const [lat, setLat] = useState<number | null>(null);
  const [lon, setLon] = useState<number | null>(null);
  const [countryCode, setCountryCode] = useState('');
  const [geocodeStatus, setGeocodeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [geocodeDisplay, setGeocodeDisplay] = useState('');
  const [enriching, setEnriching] = useState(false);
  const geocodeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset form when modal opens/closes or editing destination changes
  useEffect(() => {
    if (isOpen) {
      if (editingDestination) {
        setCity(editingDestination.city);
        setCountry(editingDestination.country);
        setRegion(editingDestination.region);
        setTagline(editingDestination.tagline || '');
        setBudgetLevel(editingDestination.budget_level || '');
        setLat(editingDestination.lat);
        setLon(editingDestination.lon);
        setCountryCode(editingDestination.country_code);
        setGeocodeStatus('success');
        setGeocodeDisplay(`${editingDestination.lat.toFixed(4)}, ${editingDestination.lon.toFixed(4)}`);
      } else {
        setCity('');
        setCountry('');
        setRegion('');
        setTagline('');
        setBudgetLevel('');
        setLat(null);
        setLon(null);
        setCountryCode('');
        setGeocodeStatus('idle');
        setGeocodeDisplay('');
      }
    }
  }, [isOpen, editingDestination]);

  const doGeocode = useCallback(async (c: string, co: string) => {
    if (!c.trim() || !co.trim()) return;
    setGeocodeStatus('loading');
    const result = await geocodeCity(c.trim(), co.trim());
    if (result) {
      setLat(result.lat);
      setLon(result.lon);
      setCountryCode(result.country_code);
      setGeocodeStatus('success');
      setGeocodeDisplay(`${result.lat.toFixed(4)}, ${result.lon.toFixed(4)}`);
    } else {
      setLat(null);
      setLon(null);
      setGeocodeStatus('error');
      setGeocodeDisplay('');
    }
  }, []);

  // Auto-geocode with debounce when city or country changes
  const triggerGeocode = useCallback(
    (c: string, co: string) => {
      if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
      if (!c.trim() || !co.trim()) {
        setGeocodeStatus('idle');
        return;
      }
      geocodeTimer.current = setTimeout(() => doGeocode(c, co), 600);
    },
    [doGeocode]
  );

  const handleCityChange = (val: string) => {
    setCity(val);
    triggerGeocode(val, country);
  };

  const handleCountryChange = (val: string) => {
    setCountry(val);
    triggerGeocode(city, val);
  };

  const canSave = city.trim() && country.trim() && region && lat !== null && lon !== null;

  const handleSave = async () => {
    if (!canSave || lat === null || lon === null) return;

    const data = {
      city: city.trim(),
      country: country.trim(),
      country_code: countryCode || '',
      region,
      lat,
      lon,
      tagline: tagline.trim(),
      budget_level: budgetLevel || undefined,
    };

    // Save basic destination to localStorage first
    let saved: CustomDestination | null;
    if (editingDestination) {
      saved = updateCustomDestination(editingDestination.id, data);
    } else {
      saved = addCustomDestination(data);
    }

    if (!saved) return;

    // Start enrichment
    setEnriching(true);
    try {
      const enrichment = await destinationsAPI.enrichCustomDestination({
        city: data.city,
        country: data.country,
        country_code: data.country_code,
        region: data.region,
        lat: data.lat,
        lon: data.lon,
      });

      // Patch localStorage entry with enrichment data
      const enriched = updateCustomDestinationEnrichment(
        saved.id,
        enrichment,
        enrichment.tagline || undefined,
        enrichment.budget_level || undefined,
      );
      if (enriched) saved = enriched;
    } catch (err) {
      console.error('Enrichment failed (destination saved without enrichment):', err);
    }

    setEnriching(false);
    onSave(saved);
    onClose();
  };

  const inputClass =
    'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent';

  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingDestination ? t('customDestinations.editTitle') : t('customDestinations.addTitle')}
      size="md"
    >
      <div className="space-y-4">
        {/* City */}
        <div>
          <label className={labelClass}>{t('customDestinations.city')} *</label>
          <input
            type="text"
            value={city}
            onChange={(e) => handleCityChange(e.target.value)}
            placeholder={t('customDestinations.cityPlaceholder')}
            className={inputClass}
          />
        </div>

        {/* Country */}
        <div>
          <label className={labelClass}>{t('customDestinations.country')} *</label>
          <input
            type="text"
            value={country}
            onChange={(e) => handleCountryChange(e.target.value)}
            placeholder={t('customDestinations.countryPlaceholder')}
            className={inputClass}
          />
        </div>

        {/* Region */}
        <div>
          <label className={labelClass}>{t('customDestinations.region')} *</label>
          <select value={region} onChange={(e) => setRegion(e.target.value)} className={inputClass}>
            <option value="">{t('customDestinations.regionPlaceholder', 'Select region')}</option>
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        {/* Tagline */}
        <div>
          <label className={labelClass}>{t('customDestinations.tagline')}</label>
          <input
            type="text"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder={t('customDestinations.taglinePlaceholder')}
            className={inputClass}
          />
        </div>

        {/* Budget Level */}
        <div>
          <label className={labelClass}>{t('customDestinations.budgetLevel')}</label>
          <select value={budgetLevel} onChange={(e) => setBudgetLevel(e.target.value)} className={inputClass}>
            <option value="">--</option>
            {BUDGET_LEVELS.map((b) => (
              <option key={b} value={b}>
                {t(`budget.levels.${b}`, b.charAt(0).toUpperCase() + b.slice(1))}
              </option>
            ))}
          </select>
        </div>

        {/* Geocode Status */}
        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            {t('customDestinations.coordinates')}
          </p>
          <div className="flex items-center gap-2">
            {geocodeStatus === 'loading' && (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-cyan-500 animate-spin" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t('customDestinations.geocoding')}
                </span>
              </>
            )}
            {geocodeStatus === 'success' && (
              <>
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <span className="text-sm text-green-600 dark:text-green-400">
                  {t('customDestinations.geocodeSuccess')} — {geocodeDisplay}
                </span>
              </>
            )}
            {geocodeStatus === 'error' && (
              <>
                <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                <span className="text-sm text-red-600 dark:text-red-400">
                  {t('customDestinations.geocodeFailed')}
                </span>
              </>
            )}
            {geocodeStatus === 'idle' && (
              <span className="text-sm text-gray-400 dark:text-gray-500">
                Enter city and country to auto-detect
              </span>
            )}
          </div>
        </div>

        {/* Enrichment Progress */}
        {enriching && (
          <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-2 border-cyan-300 dark:border-cyan-600 border-t-cyan-600 dark:border-t-cyan-300 animate-spin" />
              <span className="text-sm text-cyan-700 dark:text-cyan-300">
                Collecting weather, points of interest, and travel info. This may take a moment.
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            disabled={enriching}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {t('customDestinations.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || enriching}
            className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {enriching ? 'Enriching...' : t('customDestinations.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
